/**
 * MapView: the district-drawing workspace.
 *
 * Layout (desktop):
 *  - Left panel: plan lifecycle (create / open / check / submit / share)
 *    and reference layers + district fill opacity.
 *  - Map: place search (top-left), basemap + full-map toggle (top-right),
 *    and a bottom dock with the district palette over the drawing toolbar —
 *    the tools live next to the canvas they act on, and stay available in
 *    full-map mode. A hover tooltip shows what's under the brush.
 *  - Right dock: live district statistics (collapsible; a bottom sheet on
 *    narrow screens).
 *
 * Painting itself lives in src/map/tools/painting.ts (attached via
 * onMapReady); overlays in src/map/overlays.ts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { FeatureCollection } from 'geojson';
import maplibregl from 'maplibre-gl';
import {
  BLOCKS_HOVER_LAYER_ID,
  DEFAULT_DISTRICT_FILL_OPACITY,
  MapShell,
  setDistrictFillColors,
  setDistrictFillOpacity,
} from '../map/MapShell';
import { createPaintTool, type PaintTool } from '../map/tools/painting';
import { createOverlayManager, type OverlayManager } from '../map/overlays';
import { useTenantStore } from '../stores/tenant';
import { usePaintingStore } from '../stores/painting';
import { useOverlayStore } from '../stores/overlays';
import { tenantLayers } from '../lib/layers';
import { fetchReferenceLayers } from '../lib/plans';
import { districtLegendItems, MapLegend, UNASSIGNED_LEGEND_ITEM } from '../components/map/MapLegend';
import { MapSearch } from '../components/map/MapSearch';
import { PlanPanel } from '../components/painting/PlanPanel';
import { PaintToolbar } from '../components/painting/PaintToolbar';
import { DistrictPalette } from '../components/painting/DistrictPalette';
import { DistrictStats } from '../components/painting/DistrictStats';
import { HoverTooltip, useHoverStore } from '../components/painting/HoverTooltip';
import { LayersControl, type ReferenceStatus } from '../components/map/LayersControl';
import { BasemapControl } from '../components/map/BasemapControl';
import {
  ChartIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  CollapseIcon,
  ExpandIcon,
} from '../components/icons';

const STATS_COLLAPSE_STORAGE_KEY = 'mapview:stats-collapsed';
const SIDEBAR_COLLAPSE_STORAGE_KEY = 'mapview:sidebar-collapsed';
const FOCUS_MODE_STORAGE_KEY = 'mapview:focus-mode';
const FILL_OPACITY_STORAGE_KEY = 'mapview:fill-opacity';

function readFlag(key: string, fallback: boolean): boolean {
  try {
    const stored = window.sessionStorage.getItem(key);
    if (stored !== null) return stored === '1';
  } catch {
    // Storage unavailable — use the default.
  }
  return fallback;
}

function writeFlag(key: string, value: boolean): void {
  try {
    window.sessionStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Storage unavailable — the toggle still works for this view.
  }
}

function readFillOpacity(): number {
  try {
    const v = Number(window.localStorage.getItem(FILL_OPACITY_STORAGE_KEY));
    if (v >= 0.15 && v <= 0.95) return v;
  } catch {
    // ignore
  }
  return DEFAULT_DISTRICT_FILL_OPACITY;
}

/** Collapsed-panel rail: a slim vertical tab that reopens the panel. */
function PanelRail(props: { side: 'left' | 'right'; label: string; onOpen: () => void }): JSX.Element {
  return (
    <div className="hidden w-10 shrink-0 items-start justify-center lg:flex">
      <button
        type="button"
        onClick={props.onOpen}
        aria-expanded={false}
        aria-label={`Show ${props.label.toLowerCase()} panel`}
        title={`Show ${props.label.toLowerCase()}`}
        className="card flex flex-col items-center gap-2 px-1.5 py-3 text-stone-500 hover:text-ink"
      >
        {props.side === 'left' ? <ChevronRightIcon size={16} /> : <ChevronLeftIcon size={16} />}
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] [writing-mode:vertical-rl]">
          {props.label}
        </span>
      </button>
    </div>
  );
}

/**
 * StatsDock: the district statistics panel, docked on the RIGHT on desktop.
 * Live feedback while painting, so it stays visible during the task.
 */
function StatsDock(): JSX.Element {
  const [collapsed, setCollapsed] = useState(() => readFlag(STATS_COLLAPSE_STORAGE_KEY, false));

  function toggle(): void {
    setCollapsed((prev) => {
      writeFlag(STATS_COLLAPSE_STORAGE_KEY, !prev);
      return !prev;
    });
  }

  if (collapsed) return <PanelRail side="right" label="Stats" onOpen={toggle} />;

  return (
    <aside className="card relative hidden w-[19rem] shrink-0 flex-col overflow-hidden lg:flex">
      <div className="flex items-center justify-between border-b border-stone-100 px-4 py-2.5">
        <span className="panel-title">District statistics</span>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={true}
          aria-label="Hide district statistics"
          title="Hide statistics"
          className="btn-ghost h-7 w-7 p-0"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <DistrictStats />
      </div>
    </aside>
  );
}

/**
 * StatsBottomSheet: the district statistics as a bottom sheet on narrow
 * screens. Dismissible via the drag handle (touch), the close button, or
 * Esc. No backdrop -- the map stays visible and interactive around it.
 */
function StatsBottomSheet({ onClose }: { onClose: () => void }): JSX.Element {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const dragState = useRef<{ startY: number; deltaY: number } | null>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function onTouchStart(event: React.TouchEvent): void {
    const touch = event.touches[0];
    if (touch) dragState.current = { startY: touch.clientY, deltaY: 0 };
  }

  function onTouchMove(event: React.TouchEvent): void {
    const state = dragState.current;
    const touch = event.touches[0];
    const sheet = sheetRef.current;
    if (!state || !touch || !sheet) return;
    state.deltaY = Math.max(0, touch.clientY - state.startY);
    sheet.style.transform = `translateY(${state.deltaY}px)`;
  }

  function onTouchEnd(): void {
    const state = dragState.current;
    const sheet = sheetRef.current;
    dragState.current = null;
    if (!sheet) return;
    if (state && state.deltaY > 90) {
      onClose();
    } else {
      sheet.style.transform = '';
    }
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-2 pb-2 lg:hidden" role="dialog" aria-label="District statistics">
      <div
        ref={sheetRef}
        className="mx-auto max-h-[68vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-stone-200 bg-white shadow-2xl"
      >
        <div
          className="sticky top-0 z-10 flex touch-none select-none items-center justify-center bg-white/95 py-2.5"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <span aria-hidden="true" className="h-1.5 w-12 rounded-full bg-stone-300" />
          <span className="sr-only">Drag down to dismiss the statistics panel</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close district statistics"
            className="btn-ghost absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 p-0"
          >
            <CloseIcon size={16} />
          </button>
        </div>
        <div className="px-4 pb-5">
          <DistrictStats />
        </div>
      </div>
    </div>
  );
}

function FillOpacityControl({ value, onChange }: { value: number; onChange: (v: number) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <label className="block">
      <span className="flex items-center justify-between">
        <span className="panel-title">{t('layers.fill.title')}</span>
        <span className="text-[11px] tabular-nums text-stone-500">{Math.round(value * 100)}%</span>
      </span>
      <input
        type="range"
        min={0.15}
        max={0.95}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-[var(--tenant-primary)]"
        aria-label={t('layers.fill.aria')}
      />
      <span className="mt-0.5 block text-[11px] text-stone-400">{t('layers.fill.hint')}</span>
    </label>
  );
}

/** True at the desktop layout breakpoint (Tailwind `lg`, 1024px). */
function useDesktopLayout(): boolean {
  const query = '(min-width: 1024px)';
  const [desktop, setDesktop] = useState(() => typeof window === 'undefined' || window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = (): void => setDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return desktop;
}

export function MapView(): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  document.title = `Draw a plan — ${tenant?.name ?? 'CivicLines'}`;

  // Arrival notice after forking a shared plan: the fork is already loaded
  // into the editor by the shared view; this just orients the user.
  const location = useLocation();
  const forkNotice = (location.state as { forkedFrom?: string; hint?: string } | null) ?? null;
  const [showForkNotice, setShowForkNotice] = useState(!!forkNotice);
  useEffect(() => {
    setShowForkNotice(!!forkNotice);
  }, [forkNotice]);

  const controllersRef = useRef<{ paint: PaintTool; overlays: OverlayManager } | null>(null);
  // Staff-uploaded district maps (GeoJSON) by slug, kept for map remounts.
  const referenceDataRef = useRef<Map<string, FeatureCollection>>(new Map());
  const layers = useMemo(() => (tenant ? tenantLayers(tenant) : []), [tenant]);
  const [map, setMap] = useState<maplibregl.Map | null>(null);
  const [statsSheetOpen, setStatsSheetOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readFlag(SIDEBAR_COLLAPSE_STORAGE_KEY, false),
  );
  const [focusMode, setFocusMode] = useState(() => readFlag(FOCUS_MODE_STORAGE_KEY, false));
  const [fillOpacity, setFillOpacity] = useState(readFillOpacity);
  const fillOpacityRef = useRef(fillOpacity);
  fillOpacityRef.current = fillOpacity;

  function toggleFocusMode(): void {
    setFocusMode((prev) => {
      writeFlag(FOCUS_MODE_STORAGE_KEY, !prev);
      return !prev;
    });
  }

  function toggleSidebar(): void {
    setSidebarCollapsed((prev) => {
      writeFlag(SIDEBAR_COLLAPSE_STORAGE_KEY, !prev);
      return !prev;
    });
  }

  function changeFillOpacity(value: number): void {
    setFillOpacity(value);
    if (map) setDistrictFillOpacity(map, value);
    try {
      window.localStorage.setItem(FILL_OPACITY_STORAGE_KEY, String(value));
    } catch {
      // ignore
    }
  }

  // Keep the painting + overlay stores scoped to the current tenant, and
  // fetch block populations once per session.
  useEffect(() => {
    if (!tenant) return;
    usePaintingStore.getState().setTenant(tenant.slug, tenant);
    useOverlayStore.getState().initFromTenant(tenant.slug, tenantLayers(tenant));
    void usePaintingStore.getState().ensurePopulations();
  }, [tenant]);

  const referenceQuery = useQuery({
    queryKey: ['reference-layers', tenant?.slug],
    queryFn: fetchReferenceLayers,
    retry: false,
    staleTime: 30 * 60 * 1000,
    enabled: tenant !== null,
  });

  // Feed fetched district maps into the live overlay manager.
  useEffect(() => {
    const maps = referenceQuery.data;
    if (!maps) return;
    referenceDataRef.current = new Map(maps.map((m) => [m.slug, m.geojson]));
    for (const [slug, geojson] of referenceDataRef.current) {
      controllersRef.current?.overlays.setReferenceData(slug, geojson);
    }
  }, [referenceQuery.data]);

  const referenceStatus = useCallback(
    (slug: string): ReferenceStatus => {
      if (referenceQuery.isError) return 'missing';
      if (!referenceQuery.data) return 'loading';
      return referenceQuery.data.some((m) => m.slug === slug) ? 'ready' : 'missing';
    },
    [referenceQuery.data, referenceQuery.isError],
  );

  const handleMapReady = useCallback(
    (live: maplibregl.Map) => {
      if (!tenant) return;
      controllersRef.current?.paint.destroy();
      controllersRef.current?.overlays.destroy();

      const paint = createPaintTool(live, {
        getSourceLayer: () => tenant.map.source_layer,
        onHover: (info) =>
          useHoverStore.getState().set(
            info && info.geoid
              ? { ...info, places: controllersRef.current?.overlays.namesAt([info.x, info.y]) }
              : info,
          ),
      });
      const overlays = createOverlayManager(live, tenantLayers(tenant), {
        beforeId: BLOCKS_HOVER_LAYER_ID,
      });

      // Re-apply visibility (a remount rebuilds the layers hidden).
      const visibility = useOverlayStore.getState().visibility;
      for (const [id, visible] of Object.entries(visibility)) overlays.setVisible(id, visible);
      for (const [slug, geojson] of referenceDataRef.current) {
        overlays.setReferenceData(slug, geojson);
      }
      setDistrictFillOpacity(live, fillOpacityRef.current);
      const { colors: planColors } = usePaintingStore.getState();
      if (planColors.length > 0) setDistrictFillColors(live, planColors);
      // Repaint the active plan's assignments on the fresh map.
      usePaintingStore.getState().repaintAll();

      controllersRef.current = { paint, overlays };
      setMap(live);
    },
    [tenant],
  );

  // Tear down the controllers when this view unmounts.
  useEffect(() => {
    return () => {
      controllersRef.current?.paint.destroy();
      controllersRef.current?.overlays.destroy();
      controllersRef.current = null;
      usePaintingStore.getState().setBridge(null);
      useHoverStore.getState().set(null);
    };
  }, []);

  const handleOverlayToggle = useCallback((id: string, visible: boolean) => {
    controllersRef.current?.overlays.setVisible(id, visible);
  }, []);

  const planId = usePaintingStore((s) => s.planId);
  const desktop = useDesktopLayout();
  const planColors = usePaintingStore((s) => s.colors);
  const planLabels = usePaintingStore((s) => s.labels);
  const planDistrictCount = usePaintingStore((s) => s.districtCount);

  // The map and legend follow the open plan's district count, which can be
  // larger (or smaller) than the jurisdiction's default.
  useEffect(() => {
    if (map && planColors.length > 0) setDistrictFillColors(map, planColors);
  }, [map, planColors]);
  // Unsaved server changes: flush now and warn before the tab closes.
  useEffect(() => {
    function onBeforeUnload(event: BeforeUnloadEvent): void {
      const s = usePaintingStore.getState();
      if (!s.isLocalDraft && s.dirtyGeoids.length > 0) {
        void s.saveNow();
        event.preventDefault();
        event.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const legendItems = useMemo(
    () => [
      ...(planDistrictCount > 0
        ? districtLegendItems(planDistrictCount, planColors, planLabels)
        : districtLegendItems(tenant?.districts.count ?? 0, tenant?.districts.colors, tenant?.districts.labels)),
      UNASSIGNED_LEGEND_ITEM,
    ],
    [tenant, planDistrictCount, planColors, planLabels],
  );

  if (!tenant) {
    // Tenant resolution happens above this route; this is a defensive guard.
    return (
      <p className="text-sm text-stone-600">
        Tenant configuration is still loading. If this persists,{' '}
        <Link to="/" className="text-tenant-primary underline">
          return home
        </Link>{' '}
        and try again.
      </p>
    );
  }

  const planPanel = (
    <>
      <div className="card p-4">
        <PlanPanel tenant={tenant} />
      </div>
    </>
  );


  return (
    <div className="flex h-[calc(100dvh-5.5rem)] min-h-[560px] flex-col gap-3">
      {showForkNotice && forkNotice?.hint && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900"
        >
          <p>
            <strong>Copied{forkNotice.forkedFrom ? ` “${forkNotice.forkedFrom}”` : ''}. </strong>
            {forkNotice.hint}
          </p>
          <button
            type="button"
            onClick={() => setShowForkNotice(false)}
            aria-label="Dismiss"
            className="btn-ghost h-7 w-7 shrink-0 p-0 text-emerald-700"
          >
            <CloseIcon size={16} />
          </button>
        </div>
      )}

      {/* Desktop: side columns + map. Phones/tablets: map on top, the plan
          panel below it in its own scrolling area. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        {desktop &&
          !focusMode &&
          (sidebarCollapsed ? (
            <PanelRail side="left" label="Plan" onOpen={toggleSidebar} />
          ) : (
            <aside className="relative hidden w-[19rem] shrink-0 flex-col gap-3 overflow-y-auto pb-1 lg:flex">
              <button
                type="button"
                onClick={toggleSidebar}
                aria-expanded={true}
                aria-label="Hide plan panel"
                title="Hide panel — the map takes the page"
                className="btn-ghost absolute right-2 top-2 z-10 h-7 w-7 p-0"
              >
                <ChevronLeftIcon size={16} />
              </button>
              {planPanel}
            </aside>
          ))}

        {/* Remount the map when the config version changes so a republished
            tileset (new dataset_hash) is picked up immediately. */}
        <div
          className={`card relative shrink-0 overflow-hidden lg:h-auto lg:min-h-0 lg:flex-1 lg:shrink ${
            planId ? 'h-[44dvh]' : 'h-[42dvh]'
          }`}
        >
          <MapShell tenant={tenant} key={tenant.config_version} onMapReady={handleMapReady} />
          {map && (
            <MapSearch
              map={map}
              className="absolute left-3 top-3 z-20 w-[calc(100%-12.5rem)] max-w-[18rem] lg:w-72"
            />
          )}
          <HoverTooltip />

          <div className="absolute right-3 top-3 z-30 flex items-start gap-2">
            <BasemapControl />
            <LayersControl
              layers={layers}
              referenceStatus={referenceStatus}
              onToggle={handleOverlayToggle}
            >
              <FillOpacityControl value={fillOpacity} onChange={changeFillOpacity} />
            </LayersControl>
            <button
              type="button"
              onClick={toggleFocusMode}
              aria-pressed={focusMode}
              aria-label={focusMode ? 'Exit full-map mode' : 'Expand map to full width'}
              title={focusMode ? 'Show the side panels' : 'Full-map mode — hide the side panels'}
              className="floating hidden h-9 w-9 items-center justify-center text-stone-600 hover:text-ink lg:flex"
            >
              {focusMode ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
            </button>
          </div>

          {/* Bottom dock: district palette over the drawing toolbar. */}
          {desktop && (
            <div data-toolbar-dock className="pointer-events-none absolute inset-x-3 bottom-3 z-20 flex flex-col items-center gap-2 sm:inset-x-16">
              <div className="pointer-events-auto max-w-full">
                <DistrictPalette />
              </div>
              <div className="pointer-events-auto max-w-full">
                <PaintToolbar />
              </div>
            </div>
          )}

          {!planId && (
            <MapLegend
              items={legendItems}
              storageKey={`map-legend:${tenant.slug}`}
              className="absolute bottom-3 left-3 z-20 hidden sm:block"
            />
          )}

          {/* Below lg the stats column is hidden: open it as a bottom sheet. */}
          <div className="absolute left-3 top-16 z-20 flex flex-col gap-2 lg:hidden">
            {planId && (
              <button
                type="button"
                onClick={() => setStatsSheetOpen(true)}
                aria-label="Show district statistics"
                className="floating inline-flex h-9 items-center gap-1.5 px-3 text-sm font-semibold text-ink"
              >
                <ChartIcon size={16} /> Stats
              </button>
            )}
          </div>
        </div>

        {/* Phones/tablets: districts + drawing tools right under the map (so
            they don't cover it), then the plan panel scrolling on its own. */}
        {!desktop && planId && (
          <div data-toolbar-dock className="flex shrink-0 flex-col items-center gap-2">
            <div className="max-w-full">
              <DistrictPalette />
            </div>
            <div className="max-w-full">
              <PaintToolbar />
            </div>
          </div>
        )}
        {/* Phones/tablets: the plan panel under the map, scrolling on its own. */}
        {!desktop && (
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pb-1">{planPanel}</div>
        )}

        {desktop && !focusMode && <StatsDock />}
      </div>

      {statsSheetOpen && <StatsBottomSheet onClose={() => setStatsSheetOpen(false)} />}
    </div>
  );
}
